import ConditionalLoader from '@Guidelines/Component/Loader/ConditionalLoader';
import { theme } from '@Guidelines/Theme/theme';
import HintText from '@Guidelines/Component/HintText';
import { css } from '@emotion/react';
import * as React from 'react';

const cardStyle = css`
	display: flex;
	flex-direction: column;
	gap: 1rem;
	padding: 1.375rem;
	border-radius: ${theme.radii.xlarge};
	border: 1.5px solid ${theme.colors.border};
	background-color: ${theme.colors.surface};
`;

const headerStyle = css`
	display: flex;
	flex-direction: column;
	gap: 0.375rem;
`;

const titleRowStyle = css`
	display: flex;
	align-items: center;
	gap: 0.625rem;
	flex-wrap: wrap;
`;

const titleStyle = css`
	${theme.typography.eyebrow}
	margin: 0;
	text-transform: uppercase;
	color: ${theme.colors.inkMuted};
`;

type Props = {
	/** Absent for a card that needs no heading. */
	readonly title?: string;
	readonly description?: string;
	/** Set beside the title, e.g. a state chip. */
	readonly aside?: React.ReactNode;
	readonly children: React.ReactNode;
};

/** A white card holding one block of a form: a monospace eyebrow title, an optional explanation, then its content. */
const SectionCard: React.FC<Props> = ({ title, description, aside, children }) => {
	const titleId = React.useId();

	return (
		<section css={cardStyle} aria-labelledby={title !== undefined ? titleId : undefined}>
			<ConditionalLoader data={title}>
				{(titleText) => (
					<div css={headerStyle}>
						<div css={titleRowStyle}>
							<h2 id={titleId} css={titleStyle}>
								{titleText}
							</h2>
							{aside}
						</div>
						<ConditionalLoader data={description}>
							{(descriptionText) => <HintText>{descriptionText}</HintText>}
						</ConditionalLoader>
					</div>
				)}
			</ConditionalLoader>
			{children}
		</section>
	);
};

export default SectionCard;
